import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ACCENT, PrimaryButton, SecondaryButton } from "../artistStudio/shared";
import { isSignedIn, formatPlays, formatClock, storedUserId } from "../fanHome/fanHomeApi";
import { usePlayer, PlayPauseIcon } from "../fanHome/PlayerContext";
import MusicShell from "../fanHome/MusicShell";
import LicenceCardForm from "./LicenceCardForm";
import Waveform from "./Waveform";
import BillingAddressFields, { EMPTY_ADDRESS, addressFrom, addressPayload } from "../common/BillingAddressFields";
import { hasOffline, isOfflineSupported, removeOffline, saveOffline, subscribeOffline } from "./offlineStore";
import RightsClaimDialog from "../rightsClaims/RightsClaimDialog";
import { receiptLink } from "../receipts/receiptApi";
import { soundApi, errorCode, errorText, isUnavailable, money, formatDate, formatDateTime } from "./soundApi";

// Sound Detail Page: the sound, playback, playlists, download, licences (Personal / Creator / Business) and the videos
// that use it. Each part loads on its own, so a failing licence or video request never hides the sound itself.

const Shell = ({ children }) => <MusicShell maxWidth="max-w-4xl">{children}</MusicShell>;

const choiceKey = (id) => `pipaan.licence-choice.${id}`;
function readChoice(id) {
  try { return JSON.parse(sessionStorage.getItem(choiceKey(id)) || "{}") || {}; } catch (e) { return {}; }
}
function writeChoice(id, value) {
  try { sessionStorage.setItem(choiceKey(id), JSON.stringify(value)); } catch (e) { /* storage unavailable: the choice is just not remembered */ }
}

function BackToHome() {
  const navigate = useNavigate();
  const location = useLocation();
  // Coming from Fan Home: go back in history, so its scroll position is kept and playback carries on (one player for the whole app).
  const goBack = () => (location.state?.from === "fan-home" ? navigate(-1) : navigate("/fan/home"));
  return (
    <button type="button" onClick={goBack} data-testid="back-to-home" className="mb-5 inline-flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: ACCENT }}>
      <span aria-hidden="true">←</span> Back to Home
    </button>
  );
}

function Notice({ tone = "info", children }) {
  const style = tone === "error" ? "bg-red-50 text-red-700" : "bg-gray-50 text-gray-600";
  return <p className={`rounded-xl px-4 py-3 text-sm ${style}`} role={tone === "error" ? "alert" : undefined}>{children}</p>;
}

// ---- playlists ----------------------------------------------------------------------------------------------------
function PlaylistDialog({ soundId, onClose }) {
  const [state, setState] = useState({ loading: true, items: [], error: "" });
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    soundApi.playlists(soundId)
      .then((d) => setState({ loading: false, items: d.items || [], error: "" }))
      .catch((e) => setState({ loading: false, items: [], error: errorText(e, "We couldn't load your playlists.") }));
  }, [soundId]);
  useEffect(() => { load(); }, [load]);

  const add = async (body) => {
    setBusy(true);
    try {
      const r = await soundApi.addToPlaylist(soundId, body);
      if (r.added) toast.success(`Added to ${r.playlist.name}.`);
      else toast.info(`Already in ${r.playlist.name}.`);
      setName("");
      setCreating(false);
      load();
    } catch (e) {
      toast.error(errorText(e, "We couldn't add this sound. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-labelledby="playlist-title" data-testid="playlist-dialog">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose} aria-label="Close" className="text-2xl leading-none text-gray-500 hover:text-gray-900">×</button>
          <h2 id="playlist-title" className="flex-1 text-center text-lg font-bold text-gray-900 pr-6">Add to playlist</h2>
        </div>
        <div className="mt-4 max-h-72 overflow-auto space-y-1">
          {state.loading && <p className="text-sm text-gray-500">Loading your playlists…</p>}
          {state.error && <Notice tone="error">{state.error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button></Notice>}
          {!state.loading && !state.error && !state.items.length && <p className="text-sm text-gray-500">You have no playlists yet. Create one below.</p>}
          {state.items.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl px-2 py-2">
              <div className="h-11 w-11 shrink-0 rounded-lg bg-[#d8e4dc]" aria-hidden="true" />
              <div className="min-w-0 flex-1"><p className="font-semibold text-gray-900 truncate">{p.name}</p><p className="text-xs text-gray-500">{p.track_count} {p.track_count === 1 ? "track" : "tracks"}</p></div>
              {p.contains
                ? <span className="text-sm font-semibold" style={{ color: ACCENT }}>Added ✓</span>
                : <button type="button" disabled={busy} onClick={() => add({ playlist_id: p.id })} className="text-sm font-semibold disabled:opacity-50" style={{ color: ACCENT }}>Add</button>}
            </div>
          ))}
        </div>
        {creating ? (
          <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (name.trim()) add({ new_playlist_name: name.trim() }); }}>
            <input
              autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={150} placeholder="New playlist name" aria-label="New playlist name"
              className="flex-1 min-w-0 rounded-xl border border-gray-200 px-4 py-2 text-sm focus:outline-none focus:ring-2" style={{ "--tw-ring-color": ACCENT }}
            />
            <PrimaryButton type="submit" disabled={busy || !name.trim()} className="!px-5 !py-2 text-sm">Create</PrimaryButton>
          </form>
        ) : (
          <button type="button" onClick={() => setCreating(true)} className="mt-3 px-2 text-sm font-semibold" style={{ color: ACCENT }} data-testid="new-playlist">+ New playlist</button>
        )}
        <SecondaryButton onClick={onClose} className="mt-4 w-full !bg-green-50 !border-green-700 !text-green-900">Done</SecondaryButton>
      </div>
    </div>
  );
}

// ---- licences -----------------------------------------------------------------------------------------------------
const AI_LABEL = { human_made: "Human-made", ai_assisted: "AI-assisted", ai_generated: "AI-generated" };

function Check() { return <span className="mt-0.5 text-green-700" aria-hidden="true">✓</span>; }

function Row({ label, value, strong }) {
  return <div className="flex justify-between gap-4"><dt className="text-gray-500">{label}</dt><dd className={strong ? "font-bold text-gray-900" : "font-semibold text-gray-900"}>{value}</dd></div>;
}

// The licence panel: usage class, term, what the licence allows, then Use for Free / Buy licence. Buying goes on to the
// checkout step, and a finished purchase ends on the confirmation. Prices and rights always come from the server.
function LicenceDialog({ sound, signedIn, onClose, onChanged }) {
  const soundId = sound.id;
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  // The chosen licence type and terms are remembered for this sound, so leaving the page and coming back keeps them.
  const remembered = readChoice(soundId);
  const [selected, setSelected] = useState(remembered.selected || "personal");
  const [terms, setTerms] = useState(remembered.terms || {});
  useEffect(() => { writeChoice(soundId, { selected, terms }); }, [soundId, selected, terms]);
  const [step, setStep] = useState("options"); // options | checkout | done
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [failure, setFailure] = useState("");
  const [receipt, setReceipt] = useState(null);
  // Billing address for the sales tax: the tax is quoted again when a complete address changes, and paying waits until the
  // tax on screen is the one for the address typed.
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [showAddressErrors, setShowAddressErrors] = useState(false);
  const [quotedFor, setQuotedFor] = useState("");
  const addressKey = (a) => { const p = addressPayload(a); return p ? JSON.stringify(p) : ""; };
  const typedKey = addressKey(address);
  const addressEnabled = Boolean(step === "checkout" && summary && summary.billing_address_enabled);
  const taxPending = addressEnabled && Boolean(typedKey) && typedKey !== quotedFor;

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    soundApi.licences(soundId)
      .then((data) => setState({ loading: false, data, error: "" }))
      .catch((e) => setState({ loading: false, data: null, error: errorText(e, "We couldn't load the licence options.") }));
  }, [soundId]);
  useEffect(() => { load(); }, [load]);

  const data = state.data;
  const current = data?.classes.find((c) => c.usage_class === selected);
  const term = current ? (terms[selected] && current.terms.some((t) => t.term === terms[selected]) ? terms[selected] : current.terms[0]?.term) : null;
  const termInfo = current?.terms.find((t) => t.term === term);
  const held = (data?.my_licences || []).some((l) => l.usage_class === selected && l.term === term);

  const useFree = async () => {
    setBusy(true);
    try {
      const r = await soundApi.useForFree(soundId, { usage_class: selected, term });
      toast.success(r.already_granted ? "You already have this licence." : "Free licence added.");
      load();
      onChanged();
    } catch (e) {
      toast.error(errorText(e, "We couldn't add this licence. Please try again."));
      if (["LICENCE_UNAVAILABLE", "LICENCE_NOT_FREE"].includes(errorCode(e))) load();
    } finally {
      setBusy(false);
    }
  };

  // The address the server used (the one sent, or the one saved for this fan) is what the tax on screen is for.
  const takeQuote = (quote) => {
    setSummary(quote);
    if (quote.billing_address) {
      setQuotedFor(addressKey(addressFrom(quote.billing_address)));
      setAddress(addressFrom(quote.billing_address));
    } else {
      setQuotedFor("");
    }
  };

  const buy = async () => {
    setBusy(true);
    setFailure("");
    try {
      takeQuote(await soundApi.quote(soundId, { usage_class: selected, term, expected_price: termInfo.price, ...(addressPayload(address) ? { billing_address: addressPayload(address) } : {}) }));
      setAgreed(false);
      setUnderstood(false);
      setAutoRenew(false);
      setStep("checkout");
    } catch (e) {
      if (["PRICE_CHANGED", "LICENCE_UNAVAILABLE"].includes(errorCode(e))) { toast.error(errorText(e)); load(); }
      else toast.error(errorText(e, "We couldn't confirm the price. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  // A complete address that differs from the one the tax was worked out for: ask for the new tax (after a short pause).
  useEffect(() => {
    if (!addressEnabled || !typedKey || typedKey === quotedFor) return undefined;
    const timer = setTimeout(async () => {
      try {
        takeQuote(await soundApi.quote(soundId, { usage_class: summary.usage_class, term: summary.term, expected_price: summary.price, billing_address: addressPayload(address) }));
        setFailure("");
      } catch (e) {
        setQuotedFor(typedKey);
        setFailure(errorText(e, "We couldn't work out the sales tax. Please check the address."));
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedKey, addressEnabled, quotedFor]);

  const pay = async (outcome) => {
    const billing = addressEnabled ? addressPayload(address) : undefined;
    if (addressEnabled && !billing) { setShowAddressErrors(true); return; }
    if (taxPending) return;
    setBusy(true);
    setFailure("");
    try {
      const r = await soundApi.purchase(soundId, { usage_class: summary.usage_class, term: summary.term, expected_price: summary.price, accepted_terms: true, outcome, ...(billing ? { billing_address: billing } : {}) });
      setReceipt(r.licence);
      setStep("done");
      load();
      onChanged();
    } catch (e) {
      const code = errorCode(e);
      if (code === "PRICE_CHANGED" || code === "LICENCE_UNAVAILABLE") { toast.error(errorText(e)); setStep("options"); load(); }
      else setFailure(errorText(e, "The payment could not be completed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const needsExpiryAck = summary?.term === "one_year";
  const canPay = agreed && (!needsExpiryAck || understood);
  const closeIcon = <button type="button" onClick={onClose} aria-label="Close" className="text-2xl leading-none text-gray-500 hover:text-gray-900">×</button>;

  let body;
  if (step === "done" && receipt) {
    body = (
      <div className="text-center" data-testid="licence-confirmed">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50 text-2xl" style={{ color: ACCENT }} aria-hidden="true">✓</div>
        <h2 className="text-xl font-bold text-gray-900">Licence confirmed</h2>
        <p className="mt-2 text-sm text-gray-600">“{sound.title}” is licensed for {summary.label} use{receipt.expires_at ? ` until ${formatDate(receipt.expires_at)}` : " for life"}.</p>
        <dl className="mt-5 rounded-2xl bg-gray-50 p-4 text-sm text-left space-y-2">
          <Row label="Licence ID" value={receipt.reference} />
          <Row label="Accepted" value={formatDateTime(receipt.accepted_at)} />
          <Row label="Terms version" value={receipt.terms_version} />
        </dl>
        {receipt.id && Number(receipt.price_paid) > 0 && <Link to={receiptLink("licence", receipt.id)} data-testid="view-receipt" className="mt-5 block w-full rounded-full border-2 py-3 text-center font-semibold hover:bg-gray-50" style={{ borderColor: ACCENT, color: ACCENT }}>View receipt</Link>}
        <Link to="/fan/library?tab=licences" data-testid="view-licences" className="mt-5 block w-full rounded-full border-2 py-3 text-center font-semibold hover:bg-gray-50" style={{ borderColor: ACCENT, color: ACCENT }}>View in My Licences</Link>
        <PrimaryButton onClick={onClose} className="mt-3 w-full !py-3">Done</PrimaryButton>
      </div>
    );
  } else if (step === "checkout" && summary) {
    body = (
      <div data-testid="checkout-summary">
        <div className="flex items-center gap-3"><button type="button" onClick={() => setStep("options")} aria-label="Back to licence options" className="text-xl text-gray-500 hover:text-gray-900">←</button><h2 className="text-xl font-bold text-gray-900">Checkout</h2></div>
        <div className="mt-4 flex items-center gap-3 rounded-2xl border border-gray-200 p-3">
          {summary.cover_url ? <img src={summary.cover_url} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="h-12 w-12 rounded-lg bg-[#d8e4dc]" aria-hidden="true" />}
          <div className="min-w-0"><p className="font-semibold text-gray-900 truncate">{summary.title}</p><p className="text-xs text-gray-500">{summary.artist_name ? `${summary.artist_name} · ` : ""}{summary.label} licence, {summary.term_label}</p></div>
        </div>
        <dl className="mt-4 rounded-2xl bg-gray-50 p-4 text-sm space-y-2">
          <Row label={`${summary.label} licence — ${summary.term_label}`} value={money(summary.price, summary.currency)} />
          {summary.tax_label && <Row label={summary.tax_label} value={taxPending ? "Updating…" : summary.billing_address_required ? "Enter your billing address" : money(summary.tax_amount, summary.currency)} />}
          {summary.platform_charge > 0 && <Row label={summary.platform_charge_label || "Platform fee"} value={money(summary.platform_charge, summary.currency)} />}
          <Row label="Total" value={<span data-testid="checkout-total">{money(summary.total, summary.currency)}</span>} strong />
        </dl>
        {addressEnabled && <BillingAddressFields value={address} onChange={setAddress} showAll={showAddressErrors} disabled={busy} idPrefix="licence-billing" />}
        <label className="mt-4 flex items-start gap-2 text-sm text-gray-700"><input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" data-testid="agree-terms" /> I have read the licence terms and agree to use this sound within the {summary.label} usage class.</label>
        {needsExpiryAck && <label className="mt-2 flex items-start gap-2 text-sm text-gray-700"><input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} className="mt-1" data-testid="agree-expiry" /> I understand this licence expires on {formatDate(summary.expires_on)}.</label>}
        {summary.card_available && needsExpiryAck && <label className="mt-2 flex items-start gap-2 text-sm text-gray-700"><input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} className="mt-1" data-testid="auto-renew-option" /> Renew this licence automatically each year at the price then, using this card. I can turn this off any time in My Licences.</label>}
        <p className="mt-3 text-xs text-gray-500">{summary.scope_note}</p>
        {failure && <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="payment-error">{failure}</p>}
        {summary.card_available && (
          <>
            {addressEnabled && !addressPayload(address) && <p className="mt-3 text-xs text-gray-500" data-testid="address-needed">Enter your billing address above to pay.</p>}
            <LicenceCardForm
              soundId={soundId} summary={summary} billing={addressEnabled ? addressPayload(address) || undefined : undefined} autoRenew={autoRenew}
              canPay={canPay && !taxPending && (!addressEnabled || Boolean(addressPayload(address)))} disabled={busy}
              onPaid={(licence) => { setReceipt(licence); setStep("done"); load(); onChanged(); }}
              onProblem={(e) => { toast.error(errorText(e)); setStep("options"); load(); }}
              onFailure={setFailure}
            />
          </>
        )}
        {summary.simulation_available ? (
          <div className="mt-4 flex flex-wrap gap-3">
            <PrimaryButton onClick={() => pay("success")} disabled={busy || !canPay || taxPending} className="!py-2.5 text-sm" data-testid="simulate-success">{busy ? "Working…" : `Simulate: Pay ${money(summary.total, summary.currency)}`}</PrimaryButton>
            <SecondaryButton onClick={() => pay("declined")} disabled={busy || !canPay || taxPending} className="!py-2.5 text-sm" data-testid="simulate-declined">Simulate: Declined</SecondaryButton>
          </div>
        ) : !summary.card_available && (
          <>
            <PrimaryButton disabled className="mt-4 w-full !py-3" data-testid="pay-now">Pay {money(summary.total, summary.currency)}</PrimaryButton>
            <p className="mt-2 text-xs text-gray-500">{summary.message}</p>
          </>
        )}
      </div>
    );
  } else if (state.loading && !data) {
    body = <p className="text-sm text-gray-500">Loading licence options…</p>;
  } else if (state.error && !data) {
    body = <Notice tone="error">{state.error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button></Notice>;
  } else if (current) {
    body = (
      <>
        <p className="text-sm font-semibold text-gray-900">Usage class</p>
        <div className="mt-2 grid grid-cols-3 gap-1 rounded-full bg-gray-100 p-1" role="tablist" aria-label="Usage class">
          {data.classes.map((c) => (
            <button
              key={c.usage_class} type="button" role="tab" aria-selected={c.usage_class === selected} onClick={() => setSelected(c.usage_class)} data-testid={`licence-tab-${c.usage_class}`}
              className={`rounded-full px-3 py-2 text-sm font-semibold transition ${c.usage_class === selected ? "text-white" : "text-gray-700"}`} style={c.usage_class === selected ? { backgroundColor: ACCENT } : undefined}
            >{c.label}</button>
          ))}
        </div>
        <div data-testid={`licence-panel-${current.usage_class}`}>
          <p className="mt-3 text-sm text-gray-600">{current.available ? current.description : current.unavailable_reason}</p>
          {current.available && (
            <>
              <p className="mt-4 text-sm font-semibold text-gray-900">Term</p>
              <div className="mt-2 space-y-2">
                {current.terms.map((t) => (
                  <label key={t.term} className={`flex cursor-pointer items-center justify-between rounded-xl border px-4 py-3 text-sm ${t.term === term ? "border-green-800 bg-white" : "border-gray-200"}`}>
                    <span className="flex items-center gap-3"><input type="radio" name={`term-${selected}`} checked={t.term === term} onChange={() => setTerms((s) => ({ ...s, [selected]: t.term }))} /> <span className="font-semibold text-gray-900">{t.label}</span></span>
                    <span className="font-semibold text-gray-900" data-testid={t.term === term ? "licence-price" : undefined}>{money(t.price, data.currency)}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 rounded-2xl bg-gray-50 p-4">
                <p className="text-sm font-bold text-gray-900">What you can do</p>
                <ul className="mt-2 space-y-1.5 text-sm text-gray-700">{current.rights.map((r) => <li key={r} className="flex gap-2"><Check /> {r}</li>)}</ul>
              </div>
            </>
          )}
          <p className="mt-3 text-xs text-gray-500">Plain-language summary. <a href="/terms-conditions" target="_blank" rel="noreferrer" className="underline" style={{ color: ACCENT }}>Read full licence terms</a>. {data.scope_note}</p>
          {held && <p className="mt-3 text-sm font-semibold" style={{ color: ACCENT }} data-testid="licence-held">✓ You have this licence.</p>}
          <div className="mt-4">
            {!current.available ? (
              <PrimaryButton disabled className="w-full !py-3">Unavailable</PrimaryButton>
            ) : !signedIn ? (
              <p className="text-sm text-gray-700"><Link to="/login" className="font-semibold underline" style={{ color: ACCENT }}>Log in</Link> to {termInfo?.free ? "use this sound for free" : "buy this licence"}.</p>
            ) : termInfo?.free ? (
              <PrimaryButton onClick={useFree} disabled={busy || held} className="w-full !py-3" data-testid="use-for-free">{held ? "Licence added" : "Use for Free"}</PrimaryButton>
            ) : (
              <PrimaryButton onClick={buy} disabled={busy || !termInfo || held} className="w-full !py-3" data-testid="buy-licence">{held ? "Licence added" : `Buy licence — ${money(termInfo?.price, data.currency)}`}</PrimaryButton>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="licence-title" data-testid="licence-dialog">
      <div className="max-h-full w-full max-w-md overflow-auto rounded-3xl bg-white p-6 shadow-xl">
        {step === "options" && (
          <div className="mb-4 flex items-center gap-3">{closeIcon}<h2 id="licence-title" className="flex-1 text-center text-lg font-bold text-gray-900 pr-6">Licence “{sound.title}”</h2></div>
        )}
        {body}
      </div>
    </div>
  );
}

function LicenceCard({ sound, onOpen }) {
  const summary = sound.licence_summary || [];
  const text = summary.map((c) => {
    if (c.mode === "unavailable") return `${c.label} unavailable`;
    if (c.mode === "free") return `${c.label} free`;
    return `${c.label} from ${money(c.from_price)}`;
  }).join(" · ");
  return (
    <section className="mt-8 rounded-2xl border border-gray-200 p-5" aria-labelledby="licence-heading" data-testid="licence-section">
      <h2 id="licence-heading" className="text-lg font-bold text-gray-900">Licence this sound</h2>
      <p className="mt-1 text-sm text-gray-600" data-testid="licence-summary">{sound.licensing_available ? text : "Licensing is not available for this sound."}</p>
      <PrimaryButton onClick={onOpen} className="mt-4 !py-3 w-full sm:w-auto" data-testid="view-licence-options">View licence options</PrimaryButton>
    </section>
  );
}

// ---- videos -------------------------------------------------------------------------------------------------------
function VideosSection({ soundId }) {
  const [state, setState] = useState({ items: [], page: 0, hasMore: false, loading: true, error: "" });

  const load = useCallback((page) => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    soundApi.videos(soundId, { page, limit: 12 })
      .then((d) => setState((s) => ({ items: page === 1 ? d.items : [...s.items, ...d.items], page, hasMore: Boolean(d.has_more), loading: false, error: "" })))
      .catch((e) => setState((s) => ({ ...s, loading: false, error: errorText(e, "We couldn't load the videos.") })));
  }, [soundId]);
  useEffect(() => { load(1); }, [load]);

  return (
    <section className="mt-10" aria-labelledby="videos-heading" data-testid="videos-section">
      <h2 id="videos-heading" className="text-xl font-bold text-gray-900">Videos using this sound</h2>
      <div className="mt-4">
        {state.error && <Notice tone="error">{state.error} <button type="button" className="underline font-semibold" onClick={() => load(state.page || 1)}>Try again</button></Notice>}
        {state.loading && !state.items.length && !state.error && <p className="text-sm text-gray-500">Loading videos…</p>}
        {!state.loading && !state.error && !state.items.length && <p className="text-sm text-gray-500" data-testid="videos-empty">No videos using this sound yet.</p>}
        {state.items.length > 0 && (
          <ul className="grid gap-3 sm:grid-cols-2">
            {state.items.map((v) => (
              <li key={v.id}>
                <Link to={v.url || `/video/${v.id}`} className="flex gap-3 rounded-xl border border-gray-200 p-3 hover:bg-gray-50">
                  <VideoThumb thumbnailUrl={v.thumbnail_url} videoUrl={v.video_url} />
                  <span className="min-w-0"><span className="block font-semibold text-gray-900 truncate">@{v.creator_username}</span><span className="block text-sm text-gray-500 truncate">{v.caption}</span></span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        {state.hasMore && <div className="mt-4"><SecondaryButton onClick={() => load(state.page + 1)} disabled={state.loading} className="!px-6 !py-2 text-sm">{state.loading ? "Loading…" : "Load more"}</SecondaryButton></div>}
      </div>
    </section>
  );
}

// A small preview tile for a video: the thumbnail when there is one, otherwise the video's first frame, otherwise a
// plain tile with a play mark (so a clip that cannot be previewed never leaves an empty grey box).
function VideoThumb({ thumbnailUrl, videoUrl }) {
  const [failed, setFailed] = useState(false);
  const tile = "relative h-16 w-16 rounded-lg overflow-hidden bg-gray-200 shrink-0";
  const mark = <span className="absolute inset-0 flex items-center justify-center text-white drop-shadow" aria-hidden="true">▶</span>;
  if (thumbnailUrl) return <div className={tile}><img src={thumbnailUrl} alt="" loading="lazy" className="h-full w-full object-cover" />{mark}</div>;
  return (
    <div className={tile}>
      {videoUrl && !failed && (
        <video
          src={videoUrl} preload="metadata" muted playsInline aria-hidden="true" tabIndex={-1} className="h-full w-full object-cover pointer-events-none"
          onLoadedMetadata={(e) => { try { e.currentTarget.currentTime = 0.1; } catch (err) { /* the first frame stays as it is */ } }}
          onError={() => setFailed(true)}
        />
      )}
      {mark}
    </div>
  );
}

// ---- page ---------------------------------------------------------------------------------------------------------
export default function SoundDetail() {
  const { id } = useParams();
  const signedIn = isSignedIn();
  const player = usePlayer();
  const [state, setState] = useState({ status: "loading", sound: null, error: "" });
  const [showPlaylists, setShowPlaylists] = useState(false);
  const [showLicences, setShowLicences] = useState(false);
  const [showClaim, setShowClaim] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [offlineSaved, setOfflineSaved] = useState(false);
  const [savingSound, setSavingSound] = useState(false);
  const [likingSound, setLikingSound] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, status: "loading", error: "" }));
    soundApi.detail(id)
      .then((sound) => setState({ status: "ready", sound, error: "" }))
      .catch((e) => setState({ status: isUnavailable(e) ? "unavailable" : "error", sound: null, code: errorCode(e), error: errorText(e, "We couldn't load this sound.") }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const sound = state.sound;

  // Whether this sound is saved for offline listening in Pipaan, kept in step with the offline storage.
  const soundId = sound ? sound.id : null;
  useEffect(() => {
    if (soundId === null) return undefined;
    const sync = () => setOfflineSaved(hasOffline(soundId));
    sync();
    return subscribeOffline(sync);
  }, [soundId]);
  // A saved copy is removed as soon as the listener no longer has permission to keep it.
  const mayKeepOffline = sound && sound.download ? sound.download.allowed : null;
  useEffect(() => {
    if (soundId !== null && mayKeepOffline === false && hasOffline(soundId)) removeOffline(soundId);
  }, [soundId, mayKeepOffline]);

  if (state.status === "loading" && !sound) {
    return <Shell><BackToHome /><div className="h-48 rounded-2xl bg-gray-100 animate-pulse" aria-label="Loading" /></Shell>;
  }
  if (state.status === "unavailable") {
    return (
      <Shell><BackToHome />
        <div className="rounded-2xl bg-gray-50 px-6 py-12 text-center" data-testid="sound-unavailable">
          <h1 className="text-xl font-bold text-gray-900">{state.code === "TERRITORY_DENIED" ? "Not available in your country" : "This sound is unavailable"}</h1>
          <p className="mt-2 text-gray-600">{state.error}</p>
        </div>
      </Shell>
    );
  }
  if (state.status === "error" || !sound) {
    return (
      <Shell><BackToHome />
        <div className="rounded-2xl bg-red-50 px-6 py-12 text-center" role="alert" data-testid="sound-error">
          <h1 className="text-xl font-bold text-gray-900">We couldn't load this sound</h1>
          <p className="mt-2 text-gray-600">{state.error}</p>
          <PrimaryButton onClick={load} className="mt-4 !py-2">Try again</PrimaryButton>
        </div>
      </Shell>
    );
  }

  const current = player.isCurrent(sound.id);
  const playing = current && player.playing;
  const playError = current && player.error;
  const download = sound.download || { allowed: false };

  const onPlay = () => player.play(sound);
  const onToggleSave = async () => {
    if (savingSound) return;
    const wasSaved = Boolean(sound.saved);
    setSavingSound(true);
    setState((s) => ({ ...s, sound: { ...s.sound, saved: !wasSaved } })); // show the change at once, undo it if the call fails
    try {
      await (wasSaved ? soundApi.unsave(sound.id) : soundApi.save(sound.id));
      toast.success(wasSaved ? "Removed from saved sounds." : "Saved. It will help shape your recommendations.");
    } catch (e) {
      setState((s) => ({ ...s, sound: { ...s.sound, saved: wasSaved } }));
      toast.error(errorText(e, wasSaved ? "We couldn't remove this sound." : "We couldn't save this sound."));
    } finally {
      setSavingSound(false);
    }
  };
  const onToggleLike = async () => {
    if (likingSound) return;
    const wasLiked = Boolean(sound.liked);
    const before = Number(sound.like_count) || 0;
    setLikingSound(true);
    setState((s) => ({ ...s, sound: { ...s.sound, liked: !wasLiked, like_count: Math.max(0, before + (wasLiked ? -1 : 1)) } })); // at once, undone if the call fails
    try {
      const r = await (wasLiked ? soundApi.unlike(sound.id) : soundApi.like(sound.id));
      if (r && typeof r.like_count === "number") setState((s) => ({ ...s, sound: { ...s.sound, like_count: r.like_count } }));
    } catch (e) {
      setState((s) => ({ ...s, sound: { ...s.sound, liked: wasLiked, like_count: before } }));
      toast.error(errorText(e, wasLiked ? "We couldn't remove your like." : "We couldn't like this sound."));
    } finally {
      setLikingSound(false);
    }
  };
  // Save for offline: the server confirms the listener may keep the sound, then Pipaan stores it in its own app storage.
  // Nothing is exported as a file; the Pipaan player plays this copy.
  const onDownload = async () => {
    if (offlineSaved) {
      await removeOffline(sound.id);
      toast.info("Removed from offline listening.");
      return;
    }
    setDownloading(true);
    try {
      const file = await soundApi.download(sound.id);
      const response = await fetch(file.download_url);
      if (!response.ok) throw new Error("fetch failed");
      // The extra details are what My Library > Downloads shows; `owner` keeps one person's saved sounds from showing for another login on the same browser.
      await saveOffline(sound.id, await response.blob(), {
        title: sound.title,
        mime_type: file.mime_type,
        artist_name: sound.artists?.length ? sound.artists.map((a) => a.name).join(", ") : sound.artist_name || "",
        cover_url: sound.cover_url || null,
        audio_url: sound.audio_url || null,
        duration: sound.duration || 0,
        owner: storedUserId(),
      });
      toast.success("Saved for offline listening in Nyawoo.");
    } catch (e) {
      toast.error(errorText(e, "We couldn't save this sound for offline listening."));
      load(); // the rights may have changed
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Shell>
      <BackToHome />
      <div className="flex flex-col sm:flex-row gap-6" data-testid="sound-header">
        <div className="w-48 h-48 sm:w-56 sm:h-56 shrink-0 rounded-2xl overflow-hidden bg-gray-100">
          {sound.cover_url ? <img src={sound.cover_url} alt="" className="w-full h-full object-cover" /> : (
            <div className="w-full h-full flex items-center justify-center text-gray-300" aria-hidden="true"><svg viewBox="0 0 24 24" className="h-16 w-16" fill="currentColor"><path d="M9 18V5l12-2v13a3 3 0 1 1-2-2.83V6.3L11 7.5V18a3 3 0 1 1-2-2.83Z" /></svg></div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sound</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold text-gray-900 break-words" data-testid="sound-title">{sound.title}</h1>
          <p className="mt-1 text-lg text-gray-600" data-testid="sound-artist">
            {sound.artists?.length ? sound.artists.map((a, i) => (
              <React.Fragment key={a.id}>{i > 0 && ", "}<Link to={`/artists/${encodeURIComponent(a.slug)}`} className="hover:underline">{a.name}</Link></React.Fragment>
            )) : sound.artist_name}
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="sound-tags">
            {[...(sound.genres || []), ...(sound.moods || [])].map((tag) => <span key={tag} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">{tag}</span>)}
            {sound.ai_disclosure && AI_LABEL[sound.ai_disclosure] && <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-800">{AI_LABEL[sound.ai_disclosure]}</span>}
            {sound.explicit && <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">Explicit</span>}
            {sound.audio_quality === "high_fidelity" && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="hifi-chip" title="Lossless audio">High-Fidelity</span>}
          </div>
          <p className="mt-2 text-sm text-gray-500" data-testid="sound-stats">{[sound.duration ? formatClock(sound.duration) : "", formatPlays(sound.play_count), sound.videos_count > 0 ? `${sound.videos_count} ${sound.videos_count === 1 ? "video uses" : "videos use"} this sound` : ""].filter(Boolean).join(" · ")}</p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {sound.playback && !sound.playback.allowed ? (
              <Link
                to={signedIn ? "/fan-membership" : "/login"} data-testid="listen-locked"
                className="inline-flex items-center gap-2 rounded-full text-white font-semibold px-6 py-3" style={{ backgroundColor: ACCENT }}
              >{signedIn ? "Choose a plan to listen" : "Log in to listen"}</Link>
            ) : (
              <button
                type="button" onClick={onPlay} data-testid="play-sound" aria-label={playing ? `Pause ${sound.title}` : `Play ${sound.title}`}
                className="inline-flex items-center gap-2 rounded-full text-white font-semibold px-6 py-3" style={{ backgroundColor: ACCENT }}
              >
                {current && player.loading ? <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> : <PlayPauseIcon playing={playing} />}
                {playing ? "Pause" : "Play"}
              </button>
            )}
            <SecondaryButton onClick={() => (signedIn ? setShowPlaylists(true) : null)} disabled={!signedIn} data-testid="add-to-playlist" className="!py-3">Add to Playlist</SecondaryButton>
            <SecondaryButton onClick={() => (signedIn ? onToggleSave() : null)} disabled={!signedIn || savingSound} aria-pressed={Boolean(sound.saved)} data-testid="save-sound" className="!py-3">{sound.saved ? "Saved ✓" : "Save"}</SecondaryButton>
            <SecondaryButton onClick={() => (signedIn ? onToggleLike() : null)} disabled={!signedIn || likingSound} aria-pressed={Boolean(sound.liked)} data-testid="like-sound" className="!py-3">{sound.liked ? "♥ Liked" : "♡ Like"}{Number(sound.like_count) > 0 ? ` · ${Number(sound.like_count).toLocaleString()}` : ""}</SecondaryButton>
            <SecondaryButton onClick={onDownload} disabled={(!download.allowed && !offlineSaved) || downloading || !isOfflineSupported()} data-testid="download-sound" className="!py-3">{downloading ? "Downloading…" : offlineSaved ? "Downloaded ✓" : "Download"}</SecondaryButton>
          </div>
          {sound.playback?.allowed !== false && (
            <Waveform
              peaks={sound.waveform} label={`Waveform of ${sound.title}`}
              progress={current && player.duration ? player.position / player.duration : 0}
              onSeek={current && player.duration ? (fraction) => player.seek(fraction * player.duration) : onPlay}
            />
          )}
          {sound.playback && !sound.playback.allowed && sound.playback.message && <p className="mt-3 text-sm text-gray-600" role="status" data-testid="playback-note">{sound.playback.message}</p>}
          {playError && <p className="mt-3 text-sm text-red-600" role="alert" data-testid="play-error">{player.error} <button type="button" className="underline font-semibold" onClick={onPlay}>Try again</button></p>}
          {!signedIn && <p className="mt-3 text-sm text-gray-600"><Link to="/login" className="font-semibold underline" style={{ color: ACCENT }}>Log in</Link> to save this sound, add it to a playlist or download it.</p>}
          {signedIn && !download.allowed && download.message && <p className="mt-3 text-sm text-gray-500" data-testid="download-note">{download.message}</p>}
        </div>
      </div>

      <LicenceCard sound={sound} onOpen={() => setShowLicences(true)} />
      <VideosSection soundId={sound.id} />
      <p className="mt-8 text-sm text-gray-500" data-testid="report-sound">
        Think this sound uses your work or the rights are wrong?{" "}
        {signedIn
          ? <button type="button" onClick={() => setShowClaim(true)} className="font-semibold underline" style={{ color: ACCENT }} data-testid="open-claim">Report this sound</button>
          : <Link to="/login" className="font-semibold underline" style={{ color: ACCENT }}>Log in to report it</Link>}
      </p>
      {showPlaylists && <PlaylistDialog soundId={sound.id} onClose={() => setShowPlaylists(false)} />}
      {showClaim && <RightsClaimDialog sound={sound} onClose={() => setShowClaim(false)} />}
      {showLicences && <LicenceDialog sound={sound} signedIn={signedIn} onClose={() => setShowLicences(false)} onChanged={load} />}
    </Shell>
  );
}
